import type { JSX } from 'react';
import { describeRule } from '@logistics/contracts';
import type { WireScheduledPrompt } from '@logistics/contracts';
import { ago } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Switch } from '../shell/switch';
import { zonedTime } from './schedule-form';

export const SCHEDULE_COLUMNS =
  'grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_90px_190px] items-center gap-3.5 px-5';

const STATUS_LOOK: Readonly<Record<string, string>> = {
  ok: 'text-ok',
  partial: 'text-warn',
  failed: 'text-bad',
  skipped: 'text-mute',
};

const small =
  'border-line text-mute hover:text-ink rounded-lg border bg-transparent px-2 py-1 text-xs disabled:opacity-50';

/** One schedule: what, when, to whom, when next, how the last run went, and its actions. */
export function ScheduleRow({
  schedule,
  busy,
  onAction,
  onEdit,
  onRuns,
}: {
  schedule: WireScheduledPrompt;
  busy: boolean;
  onAction: (kind: 'toggle' | 'run' | 'remove') => void;
  onEdit: () => void;
  onRuns: () => void;
}): JSX.Element {
  const { recipients, channels } = schedule;
  const to = recipients.kind === 'all' ? 'All users' : `${recipients.userIds.length} selected`;
  const via = channels.map((c) => (c === 'telegram' ? 'TG' : 'Web')).join(' + ');
  return (
    <div
      className={cn(SCHEDULE_COLUMNS, 'border-line border-b py-3.5 transition-opacity')}
      style={{ opacity: schedule.enabled ? 1 : 0.55 }}
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{schedule.name}</div>
        <div className="text-mute truncate text-xs" title={schedule.prompt}>
          {schedule.prompt}
        </div>
      </div>
      <div className="min-w-0 text-[13px]">
        <div className="truncate">{describeRule(schedule.rule)}</div>
        <div className="text-mute truncate font-mono text-[11px]">{schedule.timezone}</div>
      </div>
      <div className="min-w-0 text-[13px]">
        <div className="truncate">{to}</div>
        <div className="text-mute font-mono text-[11px]">{via}</div>
      </div>
      <div className="text-[13px]">
        {schedule.enabled ? zonedTime(schedule.nextRunAt, schedule.timezone) : 'paused'}
      </div>
      <button type="button" onClick={onRuns} className="bg-transparent p-0 text-left text-[12.5px]">
        {schedule.lastStatus === undefined || schedule.lastRunAt === undefined ? (
          <span className="text-mute">never</span>
        ) : (
          <span className={STATUS_LOOK[schedule.lastStatus]} title="Show runs">
            {schedule.lastStatus} · {ago(schedule.lastRunAt)}
          </span>
        )}
      </button>
      <Actions schedule={schedule} busy={busy} onAction={onAction} onEdit={onEdit} />
    </div>
  );
}

function Actions({
  schedule,
  busy,
  onAction,
  onEdit,
}: {
  schedule: WireScheduledPrompt;
  busy: boolean;
  onAction: (kind: 'toggle' | 'run' | 'remove') => void;
  onEdit: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-end gap-1.5">
      <button
        type="button"
        disabled={busy || !schedule.enabled}
        onClick={() => onAction('run')}
        className={small}
      >
        Run now
      </button>
      <button type="button" disabled={busy} onClick={onEdit} className={small}>
        Edit
      </button>
      <Switch
        on={schedule.enabled}
        disabled={busy}
        label={schedule.enabled ? `Pause ${schedule.name}` : `Resume ${schedule.name}`}
        onChange={() => onAction('toggle')}
      />
      <button
        type="button"
        title="Delete"
        aria-label={`Delete ${schedule.name}`}
        disabled={busy}
        onClick={() => {
          if (window.confirm(`Delete "${schedule.name}"? Its run history goes with it.`)) {
            onAction('remove');
          }
        }}
        className="border-line text-mute hover:text-bad hover:border-bad size-7 rounded-lg border bg-transparent text-sm leading-none"
      >
        ×
      </button>
    </div>
  );
}
